import { FavoriteCity, WeatherPreferences } from './weatherPreferencesStorage';

export class WeatherPreferencesOperations {
  /**
   * Add or update a favorite city
   */
  static addOrUpdateFavoriteCity(
    preferences: WeatherPreferences,
    cityId: string,
    city: FavoriteCity,
  ): WeatherPreferences {
    return {
      ...preferences,
      favoriteCities: {
        ...preferences.favoriteCities,
        [cityId]: city,
      },
    };
  }

  /**
   * Remove a favorite city
   */
  static removeFavoriteCity(preferences: WeatherPreferences, cityId: string): WeatherPreferences {
    const updatedCities = { ...preferences.favoriteCities };
    delete updatedCities[cityId];

    return {
      ...preferences,
      favoriteCities: updatedCities,
    };
  }

  /**
   * Generate a unique ID for cities/models
   */
  static generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Find existing city by location (lat/lon with tolerance)
   */
  static findExistingCity(
    preferences: WeatherPreferences,
    location: { lat: number; lon: number; name: string },
    tolerance = 0.001, // ~100m tolerance
  ): string | null {
    for (const [cityId, city] of Object.entries(preferences.favoriteCities)) {
      const latDiff = Math.abs(city.lat - location.lat);
      const lonDiff = Math.abs(city.lon - location.lon);

      if (latDiff < tolerance && lonDiff < tolerance) {
        return cityId;
      }
    }
    return null;
  }

  /**
   * Add or update city, checking for existing location first
   */
  static addOrUpdateCityIfNotExists(
    preferences: WeatherPreferences,
    location: { lat: number; lon: number; name: string },
  ): { preferences: WeatherPreferences; cityId: string; wasAdded: boolean } {
    // Check if city already exists
    const existingCityId = this.findExistingCity(preferences, location);

    if (existingCityId) {
      // City exists, just update the name if different
      const existingCity = preferences.favoriteCities[existingCityId];
      if (existingCity.name !== location.name) {
        return {
          preferences: this.addOrUpdateFavoriteCity(preferences, existingCityId, location),
          cityId: existingCityId,
          wasAdded: false,
        };
      }
      // No changes needed
      return {
        preferences,
        cityId: existingCityId,
        wasAdded: false,
      };
    }

    // City doesn't exist, add it
    const newCityId = this.generateId();
    return {
      preferences: this.addOrUpdateFavoriteCity(preferences, newCityId, location),
      cityId: newCityId,
      wasAdded: true,
    };
  }

  /**
   * Clean up duplicate cities (same location, different IDs)
   */
  static cleanupDuplicateCities(preferences: WeatherPreferences): WeatherPreferences {
    const cleanedCities: Record<string, FavoriteCity> = {};
    const seenLocations = new Map<string, string>(); // location key -> cityId

    for (const [cityId, city] of Object.entries(preferences.favoriteCities)) {
      // Create a location key based on lat/lon (rounded to avoid floating point issues)
      const locationKey = `${Math.round(city.lat * 1000)}_${Math.round(city.lon * 1000)}`;

      if (!seenLocations.has(locationKey)) {
        // First occurrence of this location
        seenLocations.set(locationKey, cityId);
        cleanedCities[cityId] = city;
      } else {
        // Duplicate location - keep the one with the better name (longer/more descriptive)
        const existingCityId = seenLocations.get(locationKey)!;
        const existingCity = cleanedCities[existingCityId];

        if (city.name.length > existingCity.name.length) {
          // Replace with the better name
          delete cleanedCities[existingCityId];
          cleanedCities[cityId] = city;
          seenLocations.set(locationKey, cityId);
        }
        // Otherwise, ignore this duplicate
      }
    }

    return {
      ...preferences,
      favoriteCities: cleanedCities,
    };
  }
}
